"use client"

import { useEffect } from 'react'
import { usePathname } from 'next/navigation'
import { resolveCalculatorType } from '@/lib/calculator-tool-types'
import {
  isCalculatorIgnoredEvent,
  markCalculatorStarted,
  resetCalculatorStartedGuard,
} from '@/hooks/useCalculatorStarted'

/**
 * Route-group-wide `calculator_started` emitter. Mounted once from
 * app/tools/layout.tsx, so every tool page is instrumented by construction and a
 * new tool only needs an entry in CALCULATOR_TYPE_BY_TOOL_SLUG.
 *
 * Capture phase on document so a field that stops propagation still counts, and
 * so this ancestor listener claims the shared once-per-visit guard before any
 * per-tool useCalculatorStarted container listener in the same dispatch.
 *
 * Shared non-calculator input surfaces that render on tool routes opt out with
 * `data-calc-ignore` on their root; see CALC_IGNORE_SELECTOR.
 */
export function CalculatorStartedListener() {
  const pathname = usePathname()

  useEffect(() => {
    // Every run of this effect is a new visit: either a pathname change, or a
    // fresh mount after a non-tool route unmounted the tools layout. Clearing
    // first is what keeps A -> B -> A emitting twice.
    resetCalculatorStartedGuard()

    const calculatorType = resolveCalculatorType(pathname)
    if (!calculatorType || !pathname) return

    const fire = (event: Event) => {
      if (isCalculatorIgnoredEvent(event)) return
      // Detach only on a successful mark, so a rejected one never leaves a
      // visit listening-less before it emitted.
      if (!markCalculatorStarted(calculatorType, pathname)) return
      detach()
    }
    const detach = () => {
      document.removeEventListener('input', fire, true)
      document.removeEventListener('change', fire, true)
    }
    document.addEventListener('input', fire, true)
    document.addEventListener('change', fire, true)
    return detach
  }, [pathname])

  return null
}
